import { IJobStateInfoData, JobStateInfoRecord, jobStateInfoFromJSON } from "common/records/JobState/JobStateInfo";
import {
  IJobStateTransitionData,
  JobStateTransitionRecord,
  jobStateTransitionFromJSON,
} from "common/records/JobState/JobStateTransition";
import { List, Record, Map } from "immutable";

export const jobStateFromJSON = (json: Partial<IJobStateData>): JobStateRecord => {
  const config = json.state_config;
  let stateInfo = Map<string, JobStateInfoRecord>();
  if (config?.state_info) {
    Object.entries(config.state_info).forEach(([key, value]) => {
      stateInfo = stateInfo.set(key, jobStateInfoFromJSON(value));
    });
  }

  return new JobStateRecord({
    state_info: stateInfo,
    transitions: List((config?.states ?? []).map(jobStateTransitionFromJSON)),
  });
};

/** The org's full job-state config. `states` holds the transition rules (the raw `job_states.states` blob). */
export interface IJobStateConfigData {
  state_info: { [key: string]: IJobStateInfoData };
  states?: IJobStateTransitionData[];
}

export interface IJobStateData {
  state_config?: IJobStateConfigData;
}

export interface IJobStateRecord {
  state_info: Map<string, JobStateInfoRecord>;
  transitions: List<JobStateTransitionRecord>;
}

export const defaultJobStateRecord = {
  state_info: Map<string, JobStateInfoRecord>(),
  transitions: List<JobStateTransitionRecord>(),
};

export class JobStateRecord extends Record(defaultJobStateRecord) implements IJobStateRecord {}

/** Raw `job_states` blob shape persisted to the org column (what the whitelisted PATCH writes). */
export interface IJobStateBlob {
  state_info: { [slug: string]: IJobStateInfoData };
  states: IJobStateTransitionData[];
}

/** Serialize the edited record to the raw blob. `from` is written as an array (backend-equivalent to a bare string). */
export const jobStateToBlob = (js: JobStateRecord): IJobStateBlob => ({
  // Null-prototype target so a status keyed "__proto__" becomes a real own property instead of a
  // prototype write that JSON.stringify drops (which would silently lose the status on save).
  state_info: js.state_info.reduce<{ [slug: string]: IJobStateInfoData }>((acc, info, slug) => {
    acc[slug] = { pretty_name: info.pretty_name, color: info.color, archived: info.archived };
    return acc;
  }, Object.create(null)),
  states: js.transitions.map((t) => ({ from: t.from.toArray(), to: t.to, on: t.on })).toArray(),
});
